import { useToast } from "@chakra-ui/react";
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WebAPIContext } from '../contexts/WebAPIContext';
import { v4 } from 'uuid';

type UploadedFile = {
  key: string;
  fileName: string;
  lastModified: string;
  size: number;
  file: File;
  url: string | null;
  /** 読み取り専用フラグ（振り返りモード時の申請時ファイル・過去振り返りファイル） */
  isReadOnly?: boolean;
  /** マルウェアスキャン状態（THREATS_FOUND の場合 url は発行されない） */
  scanStatus?: string;
};

type UploadedFilesByFolder = {
  folderName: string;
  files: UploadedFile[];
};

/** 拡張子に対応する許可MIME Typeのマッピング */
const ALLOWED_MIME_TYPES: Record<string, string[]> = {
  'jpg':  ['image/jpeg'],
  'jpeg': ['image/jpeg'],
  'png':  ['image/png'],
  'xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  'xls':  ['application/vnd.ms-excel'],
  'csv':  ['text/csv', 'application/vnd.ms-excel'],
  'pdf':  ['application/pdf'],
};

/** デフォルトの許可拡張子（allowedExtensionsが未指定/空の場合に適用） */
const DEFAULT_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'xlsx', 'xls', 'csv', 'pdf'];

/**
 * ファイルのMIME Typeを検証する
 * @returns true: 許可, false: 拒否
 */
const validateMimeType = (file: File, ext: string): boolean => {
  const expectedMimeTypes = ALLOWED_MIME_TYPES[ext];
  if (!expectedMimeTypes) {
    // マッピングに存在しない拡張子は拒否
    return false;
  }
  // file.typeが空の場合（OSが判定できないケース）は拡張子チェックのみで許可
  if (!file.type) {
    return true;
  }
  return expectedMimeTypes.includes(file.type);
};

export const useFileService = (title: string, folderName: string, reviewDate?: string) => {
  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  // 表示情報
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadedFilesByFolder, setUploadedFilesByFolder] = useState<UploadedFilesByFolder[]>([]);
  const [deletedKeys, setDeletedKeys] = useState<string[]>([]);

  /**
   * 指定フォルダ配下のファイル情報を取得
   */
  const fetchFiles = async (withUrl: boolean = false): Promise<void> => {
    try {
      const result = await WebAPI.request({
        query: `
          query getFileInfoFromS3($folderName: String, $withUrl: Boolean, $reviewDate: String) {
            getFileInfoFromS3(folderName: $folderName, withUrl: $withUrl, reviewDate: $reviewDate) {
              folderName
              files {
                key
                fileName
                lastModified
                size
                url
                isReadOnly
                scanStatus
              }
            }
          }
        `,
        variables: { folderName, withUrl, ...(reviewDate && { reviewDate }) },
      });

      const folders = result?.data?.getFileInfoFromS3 || [];

      if (folders.length === 0) {
        return;
      }

      let files: UploadedFile[];

      if (withUrl) {
        const filesByFolder = folders.map((folder: UploadedFilesByFolder) => ({
          folderName: folder.folderName,
          files: folder.files.map(f => ({
            key: f.key,
            fileName: f.fileName,
            lastModified: f.lastModified,
            size: f.size,
            file: undefined,
            url: f.url ?? null,
            isReadOnly: f.isReadOnly ?? false,
            scanStatus: f.scanStatus ?? undefined,
          })),
        }));
        setUploadedFilesByFolder(filesByFolder);
      } else {
        files = folders[0].files.map((f: any) => ({
          key: f.key,
          fileName: f.fileName,
          lastModified: f.lastModified,
          size: f.size,
          file: undefined,
          isReadOnly: f.isReadOnly ?? false,
          scanStatus: f.scanStatus ?? undefined,
        }));
        setUploadedFiles(files);
      }

    } catch (error) {
      console.error("Exception to call API getFileInfoFromS3. error = ", error);
      toast({
        title,
        description: (error as Error).message || t('common.message.fileListFetchFailed'),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * 指定フォルダ配下のファイル情報を取得
   */
  const fetchAllBase64FromS3 = async (files: UploadedFile[] | undefined) => {

    if (!files) return [];

    // マルウェア検知ファイルはバックエンドがエラーを返すため、1件の失敗で
    // PDF出力全体が失敗しないよう allSettled で個別に扱い、取得できたものだけ返す
    const results = await Promise.allSettled(
      files.map(f =>
        WebAPI.request({
          query: `
          query getBase64FromS3($key: String!) {
            getBase64FromS3(key: $key)
          }
        `,
          variables: { key: f.key },
        }).then(res => res?.data?.getBase64FromS3)
      )
    );

    const base64Array: string[] = [];
    results.forEach((result, index) => {
      if (result.status === "fulfilled" && result.value) {
        base64Array.push(result.value);
        return;
      }
      // 取得できなかったファイルはPDFへの埋め込みを諦めて継続する
      console.warn(
        `Failed to fetch Base64, skipped: key=${files[index].key}, scanStatus=${files[index].scanStatus ?? "undefined"}`,
        result.status === "rejected" ? result.reason : undefined
      );
    });

    return base64Array; // string[]（各要素がdata:xxx;base64,yyy）
  };

  /**
   * ファイル選択処理
   * @returns 追加されたファイル数
   */
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, allowedExtensions: string[] = []): number => {
    const maxSizeMB = 1;

    const files = event.target.files;
    if (!files || files.length === 0) return 0;

    const newFiles = Array.from(files);
    const addedFiles: UploadedFile[] = [];

    const fileNames = uploadedFiles.map(f => f.fileName);

    // 許可拡張子が未指定/空の場合はデフォルトを適用
    const effectiveExtensions = allowedExtensions.length > 0 ? allowedExtensions : DEFAULT_ALLOWED_EXTENSIONS;

    for (const file of newFiles) {
      const ext = file.name.split(".").pop()?.toLowerCase();

      // 拡張子チェック
      if (!ext || !effectiveExtensions.includes(ext)) {
        toast({
          title,
          description: t('common.message.invalidFileExtension', { extensions: effectiveExtensions.join(', ') }),
          status: "error",
        });
        continue;
      }

      // MIME Typeチェック
      if (!validateMimeType(file, ext)) {
        toast({
          title,
          description: t('common.message.fileMimeTypeMismatch', { fileName: file.name }),
          status: "error",
        });
        continue;
      }

      // サイズチェック
      if (file.size > maxSizeMB * 1024 * 1024) {
        toast({
          title,
          description: t('common.message.fileSizeExceeded', { maxSizeMB }),
          status: "error",
        });
        continue;
      }

      // 重複チェック
      if (fileNames.includes(file.name)) {
        toast({
          title,
          description: t('common.message.duplicateFileName', { fileName: file.name }),
          status: "error",
        });
        continue;
      }

      addedFiles.push({
        key: `file-${v4()}`,
        fileName: file.name,
        lastModified: "",
        size: file.size,
        file,
        url: null,
      });
    }

    setUploadedFiles(prev => [...prev, ...addedFiles]);
    event.target.value = "";
    return addedFiles.length;
  };

  /**
   * アップロード先 folderName を振り返りモード時に組み替える
   * 例: "projectId/EquipmentFiles" -> "projectId/review_2026-06-01/EquipmentFiles"
   */
  const resolveUploadFolderName = (baseFolderName: string): string => {
    if (!reviewDate) return baseFolderName;
    const parts = baseFolderName.split('/');
    if (parts.length < 2) {
      // "projectId" のみの場合はそのまま review_ を追加
      return `${parts[0]}/review_${reviewDate}`;
    }
    const projectId = parts[0];
    const rest = parts.slice(1).join('/');
    return `${projectId}/review_${reviewDate}/${rest}`;
  };

  /**
   * 選択したファイルをS3にアップロード
   */
  const uploadFiles = async () => {
    try {
      const filesToUpload = uploadedFiles
        .map(f => f.file)
        .filter(Boolean) as File[];
      if (filesToUpload.length === 0 && deletedKeys.length === 0) return;

      // 振り返りモード時はアップロード先 folderName を組み替え
      const effectiveFolderName = resolveUploadFolderName(folderName);

      const CHUNK_SIZE = 1500000;
      let chunks: Array<Array<{ folderName: string; fileName: string; contentType: string; fileContent: string }>> = [];
      let currentChunk: typeof chunks[0] = [];
      let chunkSize = 0;

      for (const file of filesToUpload) {
        if (!file) continue;

        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result === "string") {
              resolve(reader.result.split(",")[1]);
            } else {
              console.error("Unexpected reader.result type:", typeof reader.result);
              reject(new Error(`Unexpected reader.result type: ${typeof reader.result}`));
            }
          };
          reader.onerror = () => reject(new Error("FileReader error"));
          reader.readAsDataURL(file);
        });

        if (chunkSize + file.size > CHUNK_SIZE) {
          chunks.push(currentChunk);
          currentChunk = [];
          chunkSize = 0;
        }

        currentChunk.push({
          folderName: effectiveFolderName,
          fileName: file.name,
          contentType: file.type,
          fileContent: base64,
        });
        chunkSize += file.size;
      }
      if (currentChunk.length > 0) chunks.push(currentChunk);

      // 並列リクエスト
      if (chunks.length > 0) {
        await Promise.all(
          chunks.map((files, index) => {
            return WebAPI.request({
              query: `
              mutation uploadMultipleFilesToS3($input: UploadMultipleFilesToS3!) {
                uploadMultipleFilesToS3(input: $input) { status, message }
              }
            `,
              variables: {
                input: {
                  files,
                  deletedKeys: index === 0 ? deletedKeys : [],
                  ...(reviewDate && { reviewDate }),
                }
              },
            });
          })
        );
      } else if (deletedKeys.length > 0) {
        await WebAPI.request({
          query: `
          mutation uploadMultipleFilesToS3($input: UploadMultipleFilesToS3!) {
            uploadMultipleFilesToS3(input: $input) { status, message }
          }
        `,
          variables: { input: { files: [], deletedKeys, ...(reviewDate && { reviewDate }) } },
        });
      }
      setDeletedKeys([]);
    } catch (error) {
      console.error("Exception to call API uploadMultipleFilesToS3. error = ", error);
      toast({
        title,
        description: t('common.message.fileUploadFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * ファイルを削除対象としてセット
   */
  const handleDelete = (file: UploadedFile) => {

    // 読み取り専用ファイル（申請時ファイル・過去振り返りファイル）は削除不可
    if (file.isReadOnly) {
      toast({
        title,
        description: t('common.message.fileReadOnly'),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (file.lastModified) {
      setDeletedKeys(prev => [...prev, file.key]);
    }
    setUploadedFiles(prev => prev.filter(f => f.key !== file.key));
  };

  return { uploadedFiles, uploadedFilesByFolder, fetchFiles, handleFileSelect, uploadFiles, handleDelete, fetchAllBase64FromS3 };
};